import { AlertTriangle } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Callout } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { CampaignReportView } from "@/features/report-campaigns";
import { ReportActions, ReportFilterPanel } from "@/features/report-filters";
import { ExportProvider, type ReportExport } from "@/features/report-kit";
import { LineReportView } from "@/features/report-lines";
import { RepReportView } from "@/features/report-reps";
import { ApiError } from "@/lib/api";
import { useReportOptions, useSaveUiPrefs, useUiPrefs } from "@/lib/queries";
import {
  PERIOD_STORAGE_KEY, REPORT_SCREENS, VIEWS_PREF, compareLabel, defaultFilters, filtersFromSearch, periodOf,
  pinView, rangeLabel, reportQuery, saveView, searchFromFilters, viewsFromPrefs, windowOf, withPeriod,
  withoutNarrowing, type ReportFilters, type ReportPeriod, type ReportTab, type SavedView,
} from "@/lib/reports";
import { useNow } from "@/lib/use-now";

/** The carried period lives in this browser only: a convenience, so storage that throws (a private
 *  window, blocked site data) just means every screen starts from the defaults. */
function readPeriod(): unknown {
  try {
    const raw = localStorage.getItem(PERIOD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePeriod(period: ReportPeriod): void {
  try {
    localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(period));
  } catch {
    // Nothing to do: the next screen opens on the defaults.
  }
}

/**
 * One report screen (ADR-074 in platform): the line, campaign or rep report, each its own page in
 * the sidebar. They share this shell — the header with the page's actions, the filter panel, the
 * saved views. Each keeps what it is narrowed to in its own URL; the period (the window, whose
 * clock, the comparison, the hours) follows the reader from one screen to the next in this browser.
 *
 * The filter lives in the URL, so a report is a link: the back button undoes a filter, a refresh
 * keeps it, and Copy link hands a colleague exactly this view. Saved views are the same URLs with a
 * name, kept in the person's own `ui_prefs`; one per screen may open when the screen is opened bare.
 */
export function ReportScreen({ kind }: { kind: ReportTab }) {
  const screen = REPORT_SCREENS[kind];
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useSearchParams();
  // A screen opened bare starts from the period the last report was read over (`withPeriod`).
  const bare = search.toString() === "";
  const filters = React.useMemo(
    () => (bare ? withPeriod(defaultFilters(), readPeriod()) : filtersFromSearch(search, defaultFilters())),
    [search, bare],
  );
  const setFilters = React.useCallback((next: ReportFilters) => setSearch(searchFromFilters(next)), [setSearch]);
  React.useEffect(() => writePeriod(periodOf(filters)), [filters]);

  // On the minute: a window that ends "now" moves once a minute, and the query key with it.
  const tick = useNow(60_000);
  const now = Math.floor(tick / 60_000) * 60_000;
  const window = React.useMemo(() => windowOf(filters, now), [filters, now]);
  const query = React.useMemo(() => reportQuery(filters, window), [filters, window]);

  const options = useReportOptions();
  const prefs = useUiPrefs();
  const savePrefs = useSaveUiPrefs();
  const allViews = React.useMemo(() => viewsFromPrefs(prefs.data), [prefs.data]);
  const views = React.useMemo(() => allViews.filter((v) => v.tab === kind), [allViews, kind]);

  // Opened bare: this screen's pinned view if it has one, else the carried period, written into
  // the URL so Copy link hands over exactly what is on screen. Once, when the views are known.
  const opened = React.useRef(false);
  React.useEffect(() => {
    if (opened.current || prefs.isPending) return;
    opened.current = true;
    if (!bare) return;
    const pinned = views.find((v) => v.pinned);
    if (pinned) navigate(`${screen.path}?${pinned.search}`, { replace: true });
    else setSearch(searchFromFilters(filters), { replace: true });
  }, [prefs.isPending, bare, views, navigate, screen.path, setSearch, filters]);

  const storeViews = async (next: SavedView[], done: string) => {
    try {
      await savePrefs.mutateAsync({ [VIEWS_PREF]: next });
      toast.success(done);
    } catch (error) {
      toast.error("Could not save", error instanceof ApiError ? error.message : "Please try again.");
      throw error;
    }
  };

  const [exports, setExports] = React.useState<ReportExport[]>([]);
  const [truncated, setTruncated] = React.useState<Record<string, boolean>>({});
  const onTruncated = React.useCallback(
    (key: string, value: boolean) => setTruncated((prev) => (prev[key] === value ? prev : { ...prev, [key]: value })),
    [],
  );
  const nameParts = React.useMemo(() => [screen.title, rangeLabel(filters)], [screen.title, filters]);
  const onDrill = React.useCallback(
    (patch: Partial<ReportFilters>) => {
      setFilters({ ...filters, ...patch });
      document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [filters, setFilters],
  );
  const props = { query, filters, compareText: compareLabel(filters), onDrill, onTruncated };

  return (
    <Page className="max-w-[1440px] space-y-4">
      <PageHeader
        className="pb-0"
        title={screen.title}
        description={`${screen.description} ${rangeLabel(filters)}, on ${filters.tz.replaceAll("_", " ")} time.`}
        action={
          <ReportActions
            screenName={screen.title.toLowerCase()}
            exports={exports}
            nameParts={nameParts}
            views={views}
            onOpenView={(v) => navigate(`${screen.path}?${v.search}`)}
            onSaveView={(name, pinned) =>
              storeViews(
                saveView(allViews, { name, tab: kind, search: searchFromFilters(filters).toString(), pinned }),
                `Saved “${name.trim()}”`,
              )
            }
            onDeleteView={(id) => void storeViews(allViews.filter((v) => v.id !== id), "View deleted").catch(() => undefined)}
            onPinView={(id) =>
              void storeViews(
                pinView(allViews, id, kind),
                id ? `Opens when you open the ${screen.title.toLowerCase()}` : "No view opens by default",
              ).catch(() => undefined)
            }
            savingView={savePrefs.isPending}
          />
        }
      />

      <ReportFilterPanel
        tab={kind}
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(withoutNarrowing(filters))}
        options={options.data}
      />

      {Object.values(truncated).some(Boolean) && (
        <Callout tone="warning">
          <span className="inline-flex items-center gap-1.5">
            <AlertTriangle className="size-4" />
            This window holds more rows than a report reads at once, so some numbers cover only its
            first part. Narrow the window or the filters for exact figures.
          </span>
        </Callout>
      )}

      <ExportProvider nameParts={nameParts} onChange={setExports}>
        {kind === "lines" && <LineReportView {...props} />}
        {kind === "campaigns" && <CampaignReportView {...props} />}
        {kind === "reps" && <RepReportView {...props} />}
      </ExportProvider>
    </Page>
  );
}
